import React, { useState, useEffect } from "react";
import telegramService from "../utils/telegram";
import apiService from "../services/api";
import { User, GamificationProfile, ReminderSettings } from "../types";
import {
  ChevronRight,
  User as UserIcon,
  Crown,
  Activity,
  Target,
  Scale,
  Ruler,
  LogOut,
  AlertCircle,
  Languages,
  Trophy,
} from "lucide-react";
import ShimmerSkeleton from "./ui/ShimmerSkeleton";
import Modal from "./ui/Modal";
import Button from "./ui/Button";
import { useToast } from "./ui/Toast";
import XPProgressBar from "./XPProgressBar";
import BadgeGrid from "./BadgeGrid";
import ReminderRow from "./ReminderRow";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigation } from "../navigation/NavigationContext";

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { push } = useNavigation();
  const [user, setUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    weight: "",
    height: "",
    age: "",
    gender: "male",
    activityLevel: "sedentary",
    dailyGoal: "",
    units: "metric",
    goal: "maintain",
  });
  const [gamification, setGamification] = useState<GamificationProfile | null>(null);
  const [reminders, setReminders] = useState<ReminderSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeDelete, setActiveDelete] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const tgId = telegramService.getUserId();
        if (tgId) {
          const [userData, gamificationData, reminderData] = await Promise.all([
            apiService.getUser(tgId),
            apiService.getGamification(tgId).catch(() => null),
            apiService.getReminders(tgId).catch(() => null),
          ]);
          setUser(userData);
          if (gamificationData) setGamification(gamificationData);
          if (reminderData) setReminders(reminderData);
          setFormData({
            weight: userData.weight?.toString() || "",
            height: userData.height?.toString() || "",
            age: userData.age?.toString() || "",
            gender: userData.gender || "male",
            activityLevel: userData.activityLevel || "sedentary",
            dailyGoal: userData.dailyGoal.toString(),
            units: userData.units || "metric",
            goal: userData.goal || "maintain",
          });
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value;
    i18n.changeLanguage(newLang);
    if (user?.tgId) {
      try {
        await apiService.updateProfile(user.tgId, { language: newLang as "uz" | "en" });
      } catch (error) {
        console.error("Error saving language:", error);
      }
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      await apiService.updateProfile(user.tgId, {
        weight: Number(formData.weight),
        height: Number(formData.height),
        age: Number(formData.age),
        gender: formData.gender as "male" | "female",
        activityLevel: formData.activityLevel as any,
        goal: formData.goal as any,
        units: formData.units as "metric" | "imperial",
      });

      if (Number(formData.dailyGoal) !== user.dailyGoal) {
        await apiService.updateGoal(user.tgId, Number(formData.dailyGoal));
      }

      telegramService.haptic("success");
      toast.show(t("common.save") + " ✓", "success");
    } catch (error) {
      console.error("Error updating profile:", error);
      telegramService.haptic("error");
      toast.show(t("common.error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setActiveDelete(false);
    telegramService.showAlert("⚠️ " + t("profile.deleteAccount"));
  };

  if (loading) return <ShimmerSkeleton variant="stats" />;

  const SectionTitle = ({ title }: { title: string }) => (
    <h3 className="section-title px-4 mb-2 mt-6">{title}</h3>
  );

  const SettingRow = ({
    icon: Icon,
    color,
    label,
    children,
  }: {
    icon: React.ElementType;
    color: string;
    label: string;
    children: React.ReactNode;
  }) => (
    <div className="relative flex items-center justify-between p-4 border-b border-line last:border-0 hover:bg-inset/50 transition-colors">
      <div className="flex items-center gap-4">
        <div className={`p-2.5 rounded-xl ${color}`}>
          <Icon size={18} />
        </div>
        <span className="font-bold text-content-primary text-sm">{label}</span>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen gradient-cream pb-32">
      {/* Header */}
      <div className="bg-surface pb-6 px-4 sticky top-0 z-10 border-b border-line shadow-card pt-[calc(1.5rem+var(--safe-top))]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-inset rounded-full flex items-center justify-center border-2 border-surface shadow-card overflow-hidden text-xl">
              {user?.gender === "female" ? "👩" : "👨"}
            </div>
            <div>
              <h1 className="text-lg font-black text-content-primary leading-tight">
                {t("profile.title")}
              </h1>
              <p className="text-xs text-content-tertiary font-medium">
                @{user?.username || "user"}
              </p>
            </div>
          </div>
          <Button variant="secondary" size="sm" loading={saving} onClick={handleSave}>
            {t("common.save")}
          </Button>
        </div>
      </div>

      {/* Premium banner */}
      <div className="px-4 mb-6 mt-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => {
            telegramService.haptic("light");
            push("premium");
          }}
          className="w-full p-4 gradient-premium rounded-2xl shadow-lg shadow-indigo-500/20 flex items-center justify-between cursor-pointer active:scale-[0.98] transition-transform"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Crown size={20} className="text-yellow-300" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">{t("premium.upgrade")}</h3>
              <p className="text-indigo-100 text-[10px] font-medium">
                {t("premium.features.unlimited")}
              </p>
            </div>
          </div>
          <ChevronRight size={20} className="text-white/50" />
        </motion.div>
      </div>

      {/* Achievements */}
      {gamification && (
        <div className="px-4 mb-2">
          <SectionTitle title={t("gamification.badges")} />
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="card overflow-hidden p-4 space-y-4"
          >
            <div className="flex items-center gap-3 pb-3 border-b border-line">
              <div className="w-10 h-10 rounded-xl gradient-warm flex items-center justify-center shadow-card">
                <Trophy size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-content-primary">
                  {t("gamification.level", { level: gamification.level })}
                </p>
                <p className="text-[10px] text-content-tertiary font-medium">
                  {t("gamification.longestStreak", { count: gamification.longestStreak })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-black text-warning">{gamification.xp} XP</p>
              </div>
            </div>
            <XPProgressBar
              xp={gamification.xp}
              level={gamification.level}
              xpToNextLevel={gamification.xpToNextLevel}
              xpForCurrentLevel={gamification.xpForCurrentLevel}
            />
            <BadgeGrid unlockedBadges={gamification.badges} />
          </motion.div>
        </div>
      )}

      <div className="px-4">
        <SectionTitle title={t("profile.personalInfo")} />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card overflow-hidden p-0"
        >
          <SettingRow icon={Scale} color="bg-info-soft text-info" label={t("stats.weight")}>
            <input
              name="weight"
              type="number"
              inputMode="numeric"
              value={formData.weight}
              onChange={handleChange}
              className="w-16 text-right font-bold text-content-primary bg-transparent outline-none placeholder:text-content-tertiary/50"
              placeholder="0"
            />
            <span className="text-xs font-bold text-content-tertiary w-4">
              {formData.units === "metric" ? "kg" : "lbs"}
            </span>
          </SettingRow>

          <SettingRow
            icon={Ruler}
            color="bg-premium-soft text-premium"
            label={t("wizard.bodyStats.height")}
          >
            <input
              name="height"
              type="number"
              inputMode="numeric"
              value={formData.height}
              onChange={handleChange}
              className="w-16 text-right font-bold text-content-primary bg-transparent outline-none placeholder:text-content-tertiary/50"
              placeholder="0"
            />
            <span className="text-xs font-bold text-content-tertiary w-4">
              {t("wizard.bodyStats.heightSuffix")}
            </span>
          </SettingRow>

          <SettingRow
            icon={UserIcon}
            color="bg-coral-soft text-coral-500"
            label={t("wizard.bodyStats.age")}
          >
            <input
              name="age"
              type="number"
              inputMode="numeric"
              value={formData.age}
              onChange={handleChange}
              className="w-16 text-right font-bold text-content-primary bg-transparent outline-none placeholder:text-content-tertiary/50"
              placeholder="0"
            />
            <span className="text-xs font-bold text-content-tertiary w-4">
              {t("wizard.bodyStats.ageSuffix")}
            </span>
          </SettingRow>

          <SettingRow
            icon={Languages}
            color="bg-success-soft text-success"
            label={t("profile.language")}
          >
            <select
              value={i18n.language}
              onChange={handleLanguageChange}
              className="bg-transparent text-right font-bold text-content-primary text-xs outline-none appearance-none cursor-pointer pr-4"
            >
              <option value="en">English</option>
              <option value="uz">O'zbekcha</option>
            </select>
            <ChevronRight
              size={14}
              className="text-content-tertiary absolute right-4 pointer-events-none"
            />
          </SettingRow>
        </motion.div>

        <SectionTitle title={t("profile.title")} />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card overflow-hidden p-0"
        >
          <SettingRow
            icon={Activity}
            color="bg-brand-soft text-brand-text"
            label={t("wizard.activity.title")}
          >
            <select
              name="activityLevel"
              value={formData.activityLevel}
              onChange={handleChange}
              className="bg-transparent text-right font-bold text-content-primary text-xs outline-none appearance-none cursor-pointer pr-4"
            >
              <option value="sedentary">{t("wizard.activity.sedentary")}</option>
              <option value="light">{t("wizard.activity.light")}</option>
              <option value="moderate">{t("wizard.activity.moderate")}</option>
              <option value="active">{t("wizard.activity.active")}</option>
              <option value="very_active">{t("wizard.activity.active")}</option>
            </select>
            <ChevronRight
              size={14}
              className="text-content-tertiary absolute right-4 pointer-events-none"
            />
          </SettingRow>

          <SettingRow
            icon={Target}
            color="bg-danger-soft text-danger"
            label={t("profile.dailyGoal")}
          >
            <div className="flex items-center bg-inset rounded-lg px-2 py-1 border border-line-strong">
              <input
                name="dailyGoal"
                type="number"
                inputMode="numeric"
                value={formData.dailyGoal}
                onChange={handleChange}
                className="w-14 text-right font-bold text-success bg-transparent outline-none"
              />
              <span className="text-[10px] font-bold text-content-tertiary ml-1">kcal</span>
            </div>
          </SettingRow>
        </motion.div>

        {/* Reminders */}
        <SectionTitle title={t("reminders.title")} />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card overflow-hidden p-0"
        >
          {(["breakfast", "lunch", "dinner", "streakReminder", "dailyReport"] as const).map(
            (key) => {
              const setting = reminders?.[key] || { enabled: false, time: "08:00" };
              return (
                <ReminderRow
                  key={key}
                  label={t(`reminders.${key}`)}
                  enabled={setting.enabled}
                  time={setting.time}
                  onToggle={(enabled) => {
                    telegramService.haptic("selection");
                    const updated = {
                      ...reminders,
                      [key]: { ...setting, enabled },
                    } as ReminderSettings;
                    setReminders(updated);
                    const tgId = telegramService.getUserId();
                    if (tgId)
                      apiService
                        .updateReminders(tgId, { [key]: { ...setting, enabled } })
                        .catch(() => {});
                  }}
                  onTimeChange={(time) => {
                    const updated = {
                      ...reminders,
                      [key]: { ...setting, time },
                    } as ReminderSettings;
                    setReminders(updated);
                    const tgId = telegramService.getUserId();
                    if (tgId)
                      apiService
                        .updateReminders(tgId, { [key]: { ...setting, time } })
                        .catch(() => {});
                  }}
                />
              );
            },
          )}
        </motion.div>

        <div className="mt-8 mb-4">
          <button
            onClick={() => setActiveDelete(true)}
            className="w-full py-4 text-danger font-bold text-sm bg-danger-soft border border-danger/20 rounded-2xl flex items-center justify-center gap-2 active:opacity-80 transition-opacity"
          >
            <LogOut size={16} /> {t("profile.deleteAccount")}
          </button>
          <p className="text-center text-[10px] text-content-tertiary mt-4 font-medium">
            version 1.2.0
          </p>
        </div>
      </div>

      <Modal
        isOpen={activeDelete}
        onClose={() => setActiveDelete(false)}
        title={`${t("profile.deleteAccount")}?`}
      >
        <div className="w-12 h-12 rounded-full bg-danger-soft text-danger flex items-center justify-center mx-auto mb-4">
          <AlertCircle size={24} />
        </div>
        <div className="flex gap-3 mt-2">
          <Button variant="secondary" size="md" fullWidth onClick={() => setActiveDelete(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" size="md" fullWidth onClick={handleDelete}>
            {t("profile.deleteAccount")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
